// Package Block
import { useState, useEffect } from "react";
import { Row, Col, DatePicker, Select as AntdSelect, notification, Tabs } from "antd";
import moment from "moment";
import { FormattedMessage } from "react-intl";
import "moment-timezone";
import { MetaTags } from "react-meta-tags";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

// Custom Components & Functions Block
import { Card, CardBody, CardHeader, CardHeaderToolbar } from "../../../../../../_metronic/_partials/controls";
import DynamicStats from "./DynamicStats";
import SectionTwo, { AgingDashboardSection } from "./../SectionTwo";
import { getAiDashboard, getDateDependDashboardStats } from "../../../_redux/dashboard/dashboardCrud";
import { dateStringApplyOffSet, getDateTimeInZone, getSettingKey, handleApiErrorResponse, toAbsoluteUrl } from "../../../../Common/utils";
import { dashboardSlice } from "../../../_redux/dashboard/dashboardSlice";
import { debounce } from "lodash";
import { findChannels } from "../../../../Inventory/_redux/warehouses/warehousesCrud";
import { useMemo } from "react";
import AiDashboard from "../ai-dashboard/AI-Dashboard";
import BasicStats from "../BasicStats";
import { checkImportApiStatus } from "../../../../Catalog/_redux/products/productsCrud";
import { CELERY_PROGRESS_STATUS_OBJECT, hasPermissions, Permission } from "../../../../Common/constants";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import { useDashboardBalanceVisibility } from "../balanceVisibility";

const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

export default function SectionOne({ dashboardFilters, setDashboardFilters, searchChannelsEntities, setSearchChannelsEntities }) {
  const { actions } = dashboardSlice;
  const dispatch = useDispatch();
  const hasAIBulletinPermission = hasPermissions([Permission.AIBulletinView])[0];
  const [stats, setStats] = useState({
    paid_amounts: 0,
    payable_amount: 0,
    receivable_amount: 0,
    total_delivery_orders: 0,
    total_pickup_orders: 0,
    total_sales: 0,
    sale_order: [],
  });
  const [purchaseOrders, setPurchaseOrders] = useState({});
  const [purchaseReceives, setPurchaseReceives] = useState({});
  const [purchaseReturns, setPurchaseReturns] = useState({});
  const [saleOrders, setSaleOrders] = useState({});
  const [saleReturn, setSaleReturn] = useState({});
  const [channels, setChannels] = useState([]);
  const [selectedChannels, setSelectedChannelsChannels] = useState([]);
  const [dashboardData, setDashboardData] = useState({});
  const [openModal, setOpenModal] = useState(false);

  const [loading, setLoading] = useState(false);
  const [loader, setLoader] = useState(false);
  const { currentState } = useSelector((state) => ({ currentState: state.basicSettings }), shallowEqual);
  const { entity, actionsLoading } = currentState;
  const timezone = getSettingKey(entity, "Basic", "timezone");
  const { showBalances, toggleBalances } = useDashboardBalanceVisibility();
  
  const [dateRange, setDateRange] = useState();
  const [targetDate, setTargetDate] = useState(getDateTimeInZone(timezone)?.split("T")[0]);
  const [startDate, setStartDate] = useState(getDateTimeInZone(timezone)?.split("T")[0]);
  const [endDate, setEndDate] = useState(getDateTimeInZone(timezone)?.split("T")[0]);
  const getPrimaryChannel = async () => {
    try {
      const response = await findChannels({ is_primary: true });
      const primaryChannel = response?.data?.entities?.[0];
      if (primaryChannel) {
        setSelectedChannelsChannels([{ label: primaryChannel.name, value: primaryChannel.id, key: primaryChannel.id }]);
      }
    } catch (err) {
      handleApiErrorResponse(err);
    }
  };

  useEffect(() => {
    getPrimaryChannel();
  }, []);

  const searchChannels = async (params) => {
    try {
      const response = await findChannels({ ...params, page_size: -1 });
      if (!params?.search_key) {
        setSearchChannelsEntities(response.data.entities);
      }
      setChannels(response.data.entities);
    } catch (err) {
      handleApiErrorResponse(err);
    }
  };

  const getDashboard = async (currentStartDate, currentEndDate) => {
    if (!currentStartDate || !currentEndDate) {
      return;
    }
    const formattedStartDate = moment(currentStartDate).format("YYYY-MM-DD");
    const formattedEndDate = moment(currentEndDate).format("YYYY-MM-DD");
    if (formattedStartDate && formattedEndDate) {
      try {
        setLoading(true);
        const start_date = dateStringApplyOffSet(formattedStartDate, timezone);
        const end_date = dateStringApplyOffSet(formattedEndDate, timezone);
        const queryParams = { start_date, end_date };
        if (selectedChannels?.length) {
          queryParams.channel_ids = selectedChannels.length == searchChannelsEntities.length ? "" : selectedChannels.map(({ value }) => value).join(",");
          setDashboardFilters((prev) => {
            return { ...prev, channel_ids: selectedChannels?.length ? selectedChannels.map(({ value }) => value) : [] };
          });
        }
        const response = await getDateDependDashboardStats(queryParams);
        setStats(response.data.sales_data);
        const purchaseOrders = transformData(response.data.inventory_data.purchase_orders);
        setPurchaseOrders(purchaseOrders);
        const purchaseReceives = transformData(response.data.inventory_data.purchase_receives);
        setPurchaseReceives(purchaseReceives);
        const purchaseReturns = transformData(response.data.inventory_data.purchase_returns);
        setPurchaseReturns(purchaseReturns);
        const saleOrders = transformData(response.data.inventory_data.sale_orders);
        setSaleOrders(saleOrders);
        const saleReturn = transformData(response.data.inventory_data.sales_return);
        setSaleReturn(saleReturn);
      } catch (err) {
        handleApiErrorResponse(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const transformData = (data) => {
    if (!data) {
      return data;
    }
    let result = {};
    data?.by_status?.forEach((item) => {
      result[item.status] = item;
    });
    data.by_status = result;
    return data;
  };

  useEffect(() => {
    if (timezone?.value && !actionsLoading && selectedChannels.length) {
      const getCurrentDate = getDateTimeInZone(timezone)?.split("T")[0];
      getDashboard(getCurrentDate, getCurrentDate);
      setStartDate(getCurrentDate);
      setEndDate(getCurrentDate);
      setDashboardFilters((prev) => {
        return { ...prev, start_date: getCurrentDate, end_date: getCurrentDate };
      });
    }
  }, [timezone, actionsLoading, selectedChannels]);

  useEffect(() => {
    searchChannels();
  }, []);

  const handleChannelSearch = useMemo(
    () => debounce((search_key) => searchChannels({ search_key }), 300), // waits 300ms after last input
    []
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      handleChannelSearch.cancel();
    };
  }, [handleChannelSearch]);

  const dashboardTabObject = {
    DASHBOARD: 1,
    AI_DASHBOARD: 2,
    AGING_DASHBOARD: 3,
  };
  const [hasOpenedAgingDashboard, setHasOpenedAgingDashboard] = useState(false);

    const taskIdKey = "dashboardTaskId";

    const fetchDashboardData = async (currentDate) => {
        setLoader(true);
        try {
            const target_date = currentDate;
            const queryParams = { target_date };
            const response = await getAiDashboard(queryParams);
            const taskId = response.data.id || response.data.task_id;
            localStorage.setItem(taskIdKey, taskId);
            checkStatus();
        } catch (err) {
            handleApiErrorResponse(err);
            setLoader(false);
        }
    };

    const checkStatus = () => {
        const taskId = localStorage.getItem(taskIdKey);
        if (taskId && taskId !== "null") {
            checkImportApiStatus(taskId)
                .then((res) => {
                    const { status: statusKey, result } = res.data.entity;

                    if (statusKey === CELERY_PROGRESS_STATUS_OBJECT.COMPLETED.id) {
                        setDashboardData(result);
                        setLoader(false);
                        localStorage.setItem(taskIdKey, "null");
                    }
                    else if (statusKey === CELERY_PROGRESS_STATUS_OBJECT.FAILED.id) {
                        // notification.error({ message: "Dashboard generation failed" });
                        setLoader(false);
                        localStorage.setItem(taskIdKey, "null");
                    }
                    else {
                        setTimeout(() => {
                            checkStatus();
                        }, 1500);
                    }
                })
                .catch((err) => {
                    console.error("Polling error:", err);
                    setLoader(false);
                });
        }
    };
    useEffect(() => {
        if (hasAIBulletinPermission) {
            fetchDashboardData(targetDate);
        }
    }, [targetDate, hasAIBulletinPermission]);

    useEffect(() => {
        if (!hasAIBulletinPermission) {
            return;
        }
        const savedTaskId = localStorage.getItem(taskIdKey);
        if (savedTaskId && savedTaskId !== "null") {
            setLoader(true);
            checkStatus();
        }
    }, [hasAIBulletinPermission]);

  const balanceToggleButton = (
    <button
      type="button"
      aria-pressed={showBalances}
      onClick={toggleBalances}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 7,
        minHeight: 34,
        padding: "7px 12px",
        color: showBalances ? "#fff" : "#444",
        background: showBalances ? "#ea1e51" : "#fff",
        border: `1px solid ${showBalances ? "#ea1e51" : "#d9d9d9"}`,
        borderRadius: 6,
        fontSize: 13,
        fontWeight: 500,
        lineHeight: 1,
        cursor: "pointer",
        marginTop: 2,
      }}
    >
      {showBalances ? <EyeOutlined /> : <EyeInvisibleOutlined />}
      <span>{showBalances ? "Hide Balance" : "Show Balance"}</span>
    </button>
  );
    
  return (
    <>
      <MetaTags>
        <title>Kommerce Hub | Dashboard</title>
        <meta
          name="description"
          content="Kommerce Hub - Empowering Commerce , On a mission to streamline your business operations and help you save your time and costs"
        />
      </MetaTags>
      <Tabs
        defaultActiveKey={dashboardTabObject.DASHBOARD}
        tabBarExtraContent={balanceToggleButton}
        onChange={(value) => {
          if (Number(value) === dashboardTabObject.AGING_DASHBOARD) {
            setHasOpenedAgingDashboard(true);
          }
        }}
      >
        <TabPane tab="Dashboard" key={dashboardTabObject.DASHBOARD}>
          <Card>
            <CardHeader title="">
              <CardHeaderToolbar>
                <div className="d-flex mt-2">
                  <div className="d-flex">
                    <label style={{
                      width: "120px",
                      marginTop: "8px"
                    }} className="mr-3"><b>Select Channel</b></label>
                    <AntdSelect
                      mode="multiple"
                      showArrow
                      allowClear
                      labelInValue
                      showSearch
                      filterOption={false}
                      placeholder="Select channels"
                      className="mr-5 border-on-select dashboard-channel-select"
                      style={{height:"34px"}}
                      value={selectedChannels}
                      onSearch={handleChannelSearch}
                      onChange={(updatedChannels) => {
                        if (!updatedChannels?.length) {
                          notification.error({ message: "You must have at least one channel selected" })
                          return;
                        }
                        handleChannelSearch("");
                        const isAllOptionClicked = updatedChannels.some(({ value }) => value === "");

                        if (isAllOptionClicked) {
                          // ✅ Case 1: If already all selected → clear all
                          if (selectedChannels.length === channels.length) {
                            notification.error({ message: "All channels already selected" });
                            return;
                          }

                          // ✅ Case 2: Select all channels
                          const all = searchChannelsEntities.map((ch) => ({
                            label: ch.name,
                            value: ch.id,
                          }));

                          setSelectedChannelsChannels(all);
                          setDashboardFilters((prev) => ({
                            ...prev,
                            channel_ids: all.map((ch) => ch.value),
                          }));
                          return;
                        }

                        // ✅ Case 3: Normal selection
                        setSelectedChannelsChannels(updatedChannels);
                        setDashboardFilters((prev) => ({
                          ...prev,
                          channel_ids: updatedChannels?.length ? updatedChannels.map(({ value }) => value) : [],
                        }));
                      }}
                    >
                      {/* All Channels Option */}
                      {channels?.length > 0 && (
                        <AntdSelect.Option value="" key="">
                          All Channels
                        </AntdSelect.Option>
                      )}

                      {/* Dynamic Channel List */}
                      {channels.map((channel) => (
                        <AntdSelect.Option key={channel.id} value={channel.id}>
                          {channel.name}
                        </AntdSelect.Option>
                      ))}
                    </AntdSelect>
                  </div>
                  <RangePicker
                    ranges={{
                      Today: [moment(getDateTimeInZone(timezone)?.split("T")[0]), moment(getDateTimeInZone(timezone)?.split("T")[0])],
                      Yesterday: [
                        moment(getDateTimeInZone(timezone)?.split("T")[0])?.subtract(1, "day"),
                        moment(getDateTimeInZone(timezone)?.split("T")[0])?.subtract(1, "day"),
                      ],
                      "Last 7 Days": [moment(getDateTimeInZone(timezone)?.split("T")[0])?.subtract(7, "days"), moment(getDateTimeInZone(timezone)?.split("T")[0])],
                      "Last 14 Days": [
                        moment(getDateTimeInZone(timezone)?.split("T")[0])?.subtract(14, "days"),
                        moment(getDateTimeInZone(timezone)?.split("T")[0]),
                      ],
                      "Last 30 Days": [
                        moment(getDateTimeInZone(timezone)?.split("T")[0])?.subtract(30, "days"),
                        moment(getDateTimeInZone(timezone)?.split("T")[0]),
                      ],
                    }}
                    style={{ height: "34px" }}
                    allowClear={false}
                    format="MM-DD-YYYY"
                    value={startDate && endDate ? [moment(startDate), moment(endDate)] : undefined}
                    onChange={(dates, dateStrings) => {
                      if (!dates || !dateStrings?.[0] || !dateStrings?.[1]) {
                        return;
                      }
                      const isoStartDate = dates[0].format("YYYY-MM-DD");
                      const isoEndDate = dates[1].format("YYYY-MM-DD");
                      setDateRange(dateStrings);
                      setStartDate(isoStartDate);
                      setEndDate(isoEndDate);
                      setDashboardFilters((prev) => {
                        return { ...prev, start_date: isoStartDate, end_date: isoEndDate };
                      });
                      dispatch(actions.dateRangeSet(dates));
                      debounce(() => {
                        getDashboard(isoStartDate, isoEndDate);
                      }, 300)();
                    }}
                  />
                </div>
              </CardHeaderToolbar>
            </CardHeader>
            <CardBody>
              <Row gutter={[10, 10]}>
                <DynamicStats loading={loading} stats={stats} />
              </Row>
              <Row className="mt-10">
                <Col span={24}>
                  <SectionTwo
                    purchase_orders={purchaseOrders}
                    purchase_receives={purchaseReceives}
                    purchase_returns={purchaseReturns}
                    sale_orders={saleOrders}
                    sales_return={saleReturn}
                    loading={loading}
                    startDate={dateStringApplyOffSet(startDate, timezone)}
                    endDate={dateStringApplyOffSet(endDate, timezone)}
                  />
                </Col>
                 <Col>
                          <BasicStats dashboardFilters={dashboardFilters} searchChannelsEntities={searchChannelsEntities} setSearchChannelsEntities={setSearchChannelsEntities} />
                        </Col>
              </Row>
            </CardBody>
          </Card>
        </TabPane>
        {hasAIBulletinPermission && <TabPane tab="Business Bulletin" key={dashboardTabObject.AI_DASHBOARD}>
          <Card >
            <CardHeader title="">
                <div style={{display:"flex", height:" 40px"}}>
              <DatePicker
                  name="target_date"
                  allowClear={false}
                  value={targetDate ? moment(targetDate) : null}
                  onChange={(date, dateString) => {
                  setTargetDate(dateString);
                  }}
                  size="small"
                  showToday={false}
              />
              <button
                        className="btn btn-primary text-weight-bold ml-3"
                        type="button"
                        onClick={() => setOpenModal(true)}
                        disabled={!dashboardData.saved_files?.txt_file}
                        title={"Daily Digest Report"}
                        style={{width:"300px"}}
                    >
                        Daily Digest Report
                        <img style={{ marginLeft: 8 }} src={toAbsoluteUrl("/media/svg/icons/Files/Download1.svg")} alt="Download" />
                    </button>
                    </div>
            </CardHeader>
            <CardBody style={{ paddingTop: "0px" }}>
                <AiDashboard dashboardData={dashboardData} openModal={openModal} setOpenModal={setOpenModal} loading={loader}/>
            </CardBody>
          </Card>
        </TabPane>}
        <TabPane tab="Aging Dashboard" key={dashboardTabObject.AGING_DASHBOARD}>
          <Card>
            <CardBody>
              {hasOpenedAgingDashboard && <AgingDashboardSection />}
            </CardBody>
          </Card>
        </TabPane>
      </Tabs>
    </>
  );
}
